import React, { useEffect, useState } from 'react';
import { LimitUsage, Org, WorkspaceLimits, orgsAPI } from '../../api/client';
import { apiErrorMessage } from '../../api/errors';
import { ErrorBanner } from '../ui';

/**
 * What this workspace is allowed to do.
 *
 * Limits are shown before anybody hits one, on purpose. A ceiling nobody can
 * see is only discovered at the moment it refuses somebody — usually while
 * they are trying to do something — and that is the least useful moment to
 * learn it exists.
 */

/** Renders a limit's number in its own unit, so storage does not read as a
 *  count of things. */
export const formatLimit = (value: number, unit: LimitUsage['unit']): string => {
  switch (unit) {
    case 'mb':
      return value >= 1024 ? `${(value / 1024).toFixed(value % 1024 === 0 ? 0 : 1)} GB` : `${value} MB`;
    case 'minutes': {
      if (value < 60 || value % 60 !== 0) return `${value} minutes`;
      const hours = value / 60;
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    case 'cpus':
      return value === 1 ? '1 CPU' : `${value} CPUs`;
    default:
      return `${value}`;
  }
};

/** The one-line reading of a limit: how much of it is gone, or that there is
 *  no ceiling at all. A self-hosted deployment has no plans, so a feature
 *  turned off there reads as off on the deployment, not as off the plan. */
export const limitSummary = (limit: LimitUsage, selfHosted = false): string => {
  if (limit.kind === 'flag') {
    if (limit.included) return 'Included';
    return selfHosted ? 'Turned off on this deployment' : 'Not on this plan';
  }
  if (limit.unlimited) {
    return limit.used === undefined
      ? 'No limit'
      : `${formatLimit(limit.used, limit.unit)} used — no limit`;
  }
  if (limit.used === undefined) return formatLimit(limit.limit, limit.unit);
  return `${formatLimit(limit.used, limit.unit)} of ${formatLimit(limit.limit, limit.unit)}`;
};

/** Fraction of a capped limit that is used, or null when there is nothing to
 *  draw a bar for. */
export const usedFraction = (limit: LimitUsage): number | null => {
  if (limit.kind === 'flag' || limit.unlimited || limit.used === undefined || limit.limit <= 0) return null;
  return Math.min(1, limit.used / limit.limit);
};

/**
 * Whether being at or near this ceiling is worth colouring as a warning.
 *
 * A personal workspace's single seat is full the moment it exists and can
 * never be anything else, so painting it red would teach people to ignore the
 * colour on the limits that do mean something.
 */
export const isAlarming = (limit: LimitUsage): boolean => {
  if (limit.fixed) return false;
  const fraction = usedFraction(limit);
  return fraction !== null && fraction >= 0.8;
};

const barColour = (fraction: number, limit: LimitUsage): string => {
  if (limit.fixed) return 'var(--text-muted)';
  if (fraction >= 1) return 'var(--danger)';
  if (fraction >= 0.8) return 'var(--warning)';
  return 'var(--primary)';
};

/** The limits a read-only workspace is past, by label, for its banner. */
const overLabels = (data: WorkspaceLimits): string | undefined =>
  data.over_plan?.map((key) => data.limits.find((l) => l.key === key)?.label || key).join(', ');

interface OrgLimitsTabProps {
  org: Org;
}

export const OrgLimitsTab: React.FC<OrgLimitsTabProps> = ({ org }) => {
  const [data, setData] = useState<WorkspaceLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    orgsAPI
      .limits(org.id)
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        if (!body || !Array.isArray(body.limits)) {
          // A response we cannot read is an error to show, not a crash: this
          // panel sits inside workspace settings and must not take the rest
          // of the page down with it.
          setError('The workspace limits could not be read.');
          return;
        }
        setData(body);
      })
      .catch((err: any) => {
        if (!cancelled) setError(`Failed to load the limits: ${apiErrorMessage(err)}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [org.id]);

  return (
    <div>
      {/* The settings pane already heads this section "Limits". */}
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0, maxWidth: 640 }}>
        What this workspace is allowed to do. Anything shown as “No limit” has
        no ceiling at all.
        {data && !data.self_hosted
          ? ' Upgrading the workspace’s plan raises these.'
          : data
            ? ' This deployment sets its own limits, so an administrator can change any of them.'
            : ''}
      </p>

      <ErrorBanner message={error} onDismiss={() => setError('')} style={{ marginBottom: 16 }} />

      {data?.read_only && (
        <div role="alert" className="card" style={{ padding: 14, borderLeft: '4px solid var(--danger)' }}>
          <strong>This workspace is read-only.</strong>
          {/* A self-hosted deployment has no plans: its ceilings are its own
              settings, so the way out is raising them, not changing plan. */}
          {data.self_hosted ? (
            <p style={{ margin: '6px 0 0', fontSize: 14 }}>
              It holds more than this deployment’s limits allow ({overLabels(data)}).
              Everything stays readable and exportable; nothing can be changed until the workspace is under its limits again or an administrator raises them in <code>OPENV_LIMITS</code>.
            </p>
          ) : (
            <p style={{ margin: '6px 0 0', fontSize: 14 }}>
              It holds more than its plan allows ({overLabels(data)}).
              Everything stays readable and exportable; nothing can be changed until the workspace is under its limits again or on a plan that fits.
              {' '}A workspace admin can subscribe on the <a href="/org/settings?tab=billing">Billing tab</a>, or remove members or delete projects.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : !data ? null : (
        <div style={{ display: 'grid', gap: 14 }}>
          {data.limits.map((limit) => {
            const fraction = usedFraction(limit);
            const alarming = isAlarming(limit);
            return (
              <div key={limit.key} className="card" style={{ padding: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                  }}
                >
                  <strong>{limit.label}</strong>
                  <span
                    style={{
                      fontSize: 13,
                      color: alarming
                        ? barColour(fraction as number, limit)
                        : limit.kind === 'flag' && limit.included === false
                          ? 'var(--text)'
                          : 'var(--text-muted)',
                      fontWeight: alarming ? 600 : 400,
                    }}
                  >
                    {limitSummary(limit, data.self_hosted)}
                  </span>
                </div>
                {fraction !== null && (
                  <div
                    role="presentation"
                    style={{
                      marginTop: 8,
                      height: 6,
                      borderRadius: 3,
                      background: 'var(--border)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.round(fraction * 100)}%`,
                        height: '100%',
                        background: barColour(fraction, limit),
                      }}
                    />
                  </div>
                )}
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  {limit.description}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
